<script setup lang="ts">
import { computed, onBeforeMount, ref, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import MultiSelect, { MultiSelectChangeEvent } from 'primevue/multiselect';
import TreeSelect from 'primevue/treeselect';
import { TreeNode } from 'primevue/tree';
import EventsReport from './EventsReport';
import ProgressReport from './ProgressReport';
import PayrollReport from './PayrollReport';
import ReviewReport from './ReviewReport';
import StudentTotalReport from './StudentTotalReport';
import AppTabMenu from '@/components/AppTabMenu';
import { useDateTime, usePersonTypes, useRouting, can, useRequestCancel, useArrayUtilities } from '@/composables';
import { useReportStore, useEventReportStore } from '@/store';
import { PERMISSIONS } from '@/enum';

const arrayUtilities = useArrayUtilities();

const $routing = useRouting();

const subOrganizationList = ref([] as TreeNode[]);
const eventReportStore = useEventReportStore();

const datesQuery = ref([new Date(), new Date()]);

const reportStore = useReportStore();
const {
  filters: {
    value: { clinicians, organizations },
  },
} = storeToRefs(reportStore);

const items = [
  {
    route: '/reports/',
    name: 'reports.events',
    label: 'Events',
    permission: [PERMISSIONS.REPORTS_VIEW_INDEX],
    component: EventsReport,
    disabled: () => $routing.route.name === 'reports.events',
  },
  {
    route: '/reports/review',
    label: 'Review',
    component: ReviewReport,
    permission: [PERMISSIONS.REPORTS_REVIEW_INDEX],
    disabled: () => $routing.route.name === 'reports.review',
  },
  {
    route: '/reports/progress',
    label: 'Progress',
    component: ProgressReport,
    permission: [PERMISSIONS.REPORTS_PROGRESS_INDEX],
    disabled: () => $routing.route.name === 'reports.progress',
  },
  {
    route: '/reports/payroll',
    label: 'Payment',
    component: PayrollReport,
    permission: [PERMISSIONS.REPORTS_PAYMENT_INDEX],
    disabled: () => $routing.route.name === 'reports.payroll',
  },
  {
    route: '/reports/student-total',
    label: 'Student Total',
    component: StudentTotalReport,
    permission: [PERMISSIONS.REPORTS_STUDENT_TOTAL_INDEX],
    disabled: () => $routing.route.name === 'reports.student-total',
  },
];

onBeforeMount(() => init());

const reportIsPayroll = computed<boolean>(() => $routing.route.name === 'reports.payroll');

const reportIsStudentTotal = computed<boolean>(() => $routing.route.name === 'reports.student-total');

onBeforeUnmount(() => {
  useRequestCancel().requestSaverAbortAll();
  $routing.router.push({ query: {} });
});

const reportsIsEvents = computed<boolean>(() => {
  return $routing.route.name === 'reports.events';
});

const itemsEnabled = computed(() => {
  return items.filter((item) => {
    if (item.permission) {
      return can(item.permission);
    }

    return false;
  });
});

const filteredClinician = ref(false);

const showClearFilters = computed(() => {
  if (reportIsPayroll.value) {
    return Object.keys($routing.route.query).length > 2;
  }

  if (can([PERMISSIONS.REPORTS_SHOW_CLEAR_FILTERS_CLINICIAN])) {
    return Object.keys($routing.route.query).length > 5;
  }
  return Object.keys($routing.route.query).length > 3;
});

const disabledClinicianOptions = computed<string>(() => (usePersonTypes.isClinician() ? 'name' : ''));

const displaySelectedClinicianType = computed<string>(() =>
  can([PERMISSIONS.REPORTS_DISPLAY_SELECTED_CLINICIAN_TYPE]) ? 'comma' : 'chip',
);

const init = async () => {
  await reportStore.defineDefaults();
  const dates = reportStore.baseDates();

  datesQuery.value[0] = dates.start;
  datesQuery.value[1] = dates.end;

  if (can([PERMISSIONS.REPORTS_FILTER_ORGANIZATIONS]))
    await reportStore.getReportOrganizationFilter(dates.start, dates.end);
  if (can([PERMISSIONS.REPORTS_FILTER_SERVICE_PROVIDERS]))
    await reportStore.getReportServiceProviderFilter(dates.start, dates.end);
};

const onClinicianChange = (event: MultiSelectChangeEvent) => {
  const selectedClinician = event.value;
  filteredClinician.value = selectedClinician.length;

  if (selectedClinician.length) {
    $routing.addQueryParam('clinicians', selectedClinician.join());
  } else {
    reportStore.filters.clinicians.selected = [];
    $routing.removeQueryParam('clinicians');
  }
};

const onClearFilters = async () => {
  const dates = reportStore.baseDates(reportsIsEvents.value);
  const start = dates.start;
  const end = dates.end;
  const startAt = useDateTime().formatDateForApi(start);
  const endAt = useDateTime().formatDateForApi(end);
  const defaultFilters = {
    start_at: startAt,
    end_at: endAt,
  };
  let resetQueryParams =
    can([PERMISSIONS.REPORTS_RSM_INDEX]) && !reportIsPayroll.value
      ? {
          ...defaultFilters,
          is_rsm: 0,
        }
      : defaultFilters;
  const clinicianDefault = can([PERMISSIONS.REPORTS_SHOW_CLEAR_FILTERS_CLINICIAN])
    ? reportStore.filters.clinicians.selected
    : [];
  reportStore.filters.dates = [start, end];
  reportStore.filters.organizations.selected = {};
  reportStore.filters.organizations.isRsm = false;

  if (can([PERMISSIONS.REPORTS_FILTER_SERVICE_PROVIDERS])) {
    await reportStore.getReportServiceProviderFilter(start, end);
    reportStore.filters.clinicians.selected = [];
  }

  if (clinicianDefault && clinicianDefault.length > 0) {
    resetQueryParams = { ...resetQueryParams, clinicians: clinicianDefault };
  }
  $routing.resetQueryParams(resetQueryParams);
};

const onOrganizationChange = async () => {
  subOrganizationList.value.forEach((org) => {
    if (organizations.selected !== null) {
      organizations.selected[org.key] = true;
    }
  });

  subOrganizationList.value = [];

  reportStore.filters.organizations.isRsm = isThereRsmSelected();

  if (can([PERMISSIONS.REPORTS_CHANGE_ORGANIZATIONS_CLINICIAN])) {
    reportStore.filters.clinicians.selected = [];
    $routing.removeQueryParam('clinicians');
  }

  if (can([PERMISSIONS.REPORTS_FILTER_SERVICE_PROVIDERS])) {
    const dates = datesQuery.value;
    await reportStore.getReportServiceProviderFilter(dates[0], dates[1]);
  }

  const params = Object.keys(organizations.selected).join();

  if (params.length > 0) {
    $routing.addQueryParam('organizations', params);
  } else {
    $routing.removeQueryParam('organizations');
  }
};

const onOrganizationSelected = async (event: TreeNode) => {
  const subOrganizations = arrayUtilities.useFlatten(event.children);
  subOrganizationList.value = subOrganizations;
};

const dateRangeChange = async (dates: Date[]) => {
  reportStore.onDateRangeChange(dates);
  eventReportStore.filters.searchingByDates = !eventReportStore.filters.searchingByDates;

  datesQuery.value = dates;

  if (can([PERMISSIONS.REPORTS_FILTER_ORGANIZATIONS]))
    await reportStore.getReportOrganizationFilter(dates[0], dates[1]);

  const canChangeClinicians =
    can([PERMISSIONS.REPORTS_FILTER_SERVICE_PROVIDERS]) &&
    (usePersonTypes.isElumaAdmin() || usePersonTypes.isSchoolAdmin());

  if (canChangeClinicians) {
    await reportStore.getReportServiceProviderFilter(dates[0], dates[1]);
  }
};

const isThereRsmSelected = () => {
  const flatOrganizationList = arrayUtilities.useFlatten(organizations.options);
  const selectedOrganizations = Object.keys(organizations.selected).map((organization) => Number(organization));
  const filter = flatOrganizationList.filter((organization) => selectedOrganizations.includes(organization.key));
  return filter.every((option) => option.isRsm === true);
};

const activeIndex = computed(() => items.findIndex((item) => item.disabled()));
</script>

<template>
  <div class="page-container">
    <div class="page-header page-header-with-tabs">
      <h1>Reports</h1>
      <div class="page-header-tab-bar flex-wrap gap-2 justify-content-between">
        <AppTabMenu :items="itemsEnabled" :active-index="activeIndex" />
        <div v-show="!reportIsStudentTotal">
          <div class="flex flex-wrap gap-2">
            <AppButton v-if="showClearFilters" type="secondary" text="Clear" @click="onClearFilters" />
            <TreeSelect
              v-if="can([PERMISSIONS.REPORTS_FILTER_SERVICE_PROVIDERS])"
              v-model="organizations.selected"
              :options="organizations.options"
              selection-mode="multiple"
              :meta-key-selection="false"
              display="chip"
              placeholder="All Organizations"
              :disabled="reportIsStudentTotal"
              @change="onOrganizationChange"
              @node-select="onOrganizationSelected"
            />
            <MultiSelect
              v-model="clinicians.selected"
              :options="clinicians.options"
              :loading="clinicians.loading"
              :filter="true"
              :reset-filter-on-hide="true"
              :option-disabled="disabledClinicianOptions"
              remove-token-icon="m-icon close text-sm"
              option-label="name"
              option-value="value"
              :show-toggle-all="false"
              placeholder="All Service Providers"
              :display="displaySelectedClinicianType"
              class="z-1"
              :disabled="reportIsStudentTotal"
              @change="onClinicianChange"
            />
            <AppDateRangePicker
              v-if="can([PERMISSIONS.REPORTS_SELECT_DATES_VIEW])"
              v-model="reportStore.filters.dates"
              :value="reportStore.filters.dates"
              :disabled="reportIsPayroll || reportIsStudentTotal"
              @change="dateRangeChange"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="page-container">
      <div class="w-full">
        <RouterView @define-query-params="reportStore.defineQueryParams" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tab-menu {
  position: relative;
  top: -48px;
  left: 10px;
}

:deep(.p-treeselect) {
  .p-treeselect-label {
    font-weight: bold;
    &.p-placeholder {
      color: $grayDark;
    }
  }
  .p-treeselect-label-container {
    width: 100%;
    max-width: 12rem;
  }
}

:deep(.p-multiselect) {
  .p-multiselect-label {
    font-weight: bold;
    &.p-placeholder {
      color: $grayDark;
    }
  }
  .p-multiselect-label-container {
    width: 100%;
    max-width: 12rem;
  }
}
</style>
